/**
 * Chat flutuante: bolinha no canto inferior direito (com não lidas), painel de conversas e até 3 janelinhas abertas
 * lado a lado (minimizáveis), que continuam abertas ao navegar. Aviso no canto e do navegador quando chega mensagem.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { navigate, useLocation } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { onLive } from "@/lib/live";
import { Avatar, Icon } from "./icons";
import { ConversationView, NewConvModal, type Conv } from "@/pages/ChatPage";

interface DockState { open: boolean; wins: Array<{ id: string; min: boolean }> }
const KEY = "vg.chat.dock";
const load = (): DockState => { try { return { open: false, wins: [], ...JSON.parse(localStorage.getItem(KEY) ?? "{}") } as DockState; } catch { return { open: false, wins: [] }; } };
const store = (s: DockState) => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* sem storage */ } };
const NOTIF_KEY = "vg.chat.notif";

export function ChatDock(): JSX.Element | null {
  const { path } = useLocation();
  const qc = useQueryClient();
  const { me } = useLookups();
  const [st, setSt] = useState<DockState>(load);
  const [q, setQ] = useState("");
  const [nova, setNova] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: string; conv: Conv }>>([]);
  const [notif, setNotif] = useState(() => { try { return localStorage.getItem(NOTIF_KEY) === "1" && typeof Notification !== "undefined" && Notification.permission === "granted"; } catch { return false; } });
  const { data: sum } = useQuery({ queryKey: ["chat-resumo"], queryFn: () => api.get<{ unread: number; mentions: number }>("/chat/resumo"), refetchInterval: 120_000 });
  const { data: convs } = useQuery({ queryKey: ["chat-conversas"], queryFn: () => api.get<Conv[]>("/chat/conversas"), refetchInterval: 120_000 });
  const set = (s: DockState) => { setSt(s); store(s); };
  const stRef = useRef(st); stRef.current = st;
  const convsRef = useRef(convs); convsRef.current = convs;

  const openConv = (id: string) => {
    const cur = stRef.current;
    const wins = [{ id, min: false }, ...cur.wins.filter((w) => w.id !== id)].slice(0, 3);
    set({ open: false, wins });
  };

  // Mensagem nova: aviso no canto (se a conversa não estiver aberta) e do navegador (se a aba estiver em segundo plano).
  useEffect(() => onLive(async (e) => {
    if (e.t !== "msg" || !e.by || e.by === me?.id) return;
    const list = await qc.fetchQuery({ queryKey: ["chat-conversas"], queryFn: () => api.get<Conv[]>("/chat/conversas"), staleTime: 0 });
    const c = list.find((x) => x.id === e.c);
    if (!c || !c.unread || c.kind === "bot") return;
    const visible = stRef.current.wins.some((w) => w.id === c.id && !w.min) || (window.location.pathname === "/chat" && new URLSearchParams(window.location.search).get("c") === c.id);
    if (!visible && window.location.pathname !== "/chat") {
      const tid = `${c.id}:${Date.now()}`;
      setToasts((t) => [...t.filter((x) => x.conv.id !== c.id), { id: tid, conv: c }].slice(-3));
      setTimeout(() => setToasts((t) => t.filter((x) => x.id !== tid)), 8000);
    }
    if (document.hidden && notif && typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        const n = new Notification(c.title, { body: c.last ? `${c.last.who}: ${c.last.body}` : "Nova mensagem", tag: c.id });
        n.onclick = () => { window.focus(); openConv(c.id); n.close(); };
      } catch { /* navegador sem suporte */ }
    }
  }), [me?.id, notif, qc]);

  const toggleNotif = async () => {
    if (notif) { setNotif(false); try { localStorage.setItem(NOTIF_KEY, "0"); } catch { /* */ } return; }
    if (typeof Notification === "undefined") return;
    const p = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
    const on = p === "granted";
    setNotif(on);
    try { localStorage.setItem(NOTIF_KEY, on ? "1" : "0"); } catch { /* */ }
  };

  if (path === "/chat") return null;
  const list = (convs ?? []).filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (a.kind === "bot" ? -1 : b.kind === "bot" ? 1 : (b.lastAt ?? "").localeCompare(a.lastAt ?? "")));
  const titleOf = (id: string) => convs?.find((c) => c.id === id)?.title ?? "Conversa";
  const unreadOf = (id: string) => convs?.find((c) => c.id === id)?.unread ?? 0;
  return (
    <>
      <div className="dock-wins">
        {st.wins.map((w) => (
          <div key={w.id} className={`dock-win ${w.min ? "min" : ""}`}>
            <div className="dock-win-head" onClick={() => set({ ...st, wins: st.wins.map((x) => (x.id === w.id ? { ...x, min: !x.min } : x)) })}>
              <b className="ell">{titleOf(w.id)}</b>
              {w.min && unreadOf(w.id) > 0 && <span className="nav-badge">{unreadOf(w.id)}</span>}
              <span className="spacer" />
              <button title="Abrir em tela cheia" onClick={(e) => { e.stopPropagation(); set({ ...st, wins: st.wins.filter((x) => x.id !== w.id) }); navigate(`/chat?c=${w.id}`); }}>⤢</button>
              <button title={w.min ? "Abrir" : "Minimizar"}>{w.min ? "▴" : "▾"}</button>
              <button title="Fechar" onClick={(e) => { e.stopPropagation(); set({ ...st, wins: st.wins.filter((x) => x.id !== w.id) }); }}>✕</button>
            </div>
            {!w.min && <div className="dock-win-body"><ConversationView id={w.id} compact dock /></div>}
          </div>
        ))}
      </div>
      {st.open && (
        <div className="dock-panel">
          <div className="dock-panel-head">
            <b>Chat</b>
            <span className="spacer" />
            <button title={notif ? "Avisos do navegador ligados" : "Ligar avisos do navegador"} className={notif ? "on" : ""} onClick={() => void toggleNotif()}>{notif ? "🔔" : "🔕"}</button>
            <button title="Nova conversa" onClick={() => setNova(true)}><Icon name="plus" size="sm" /></button>
            <button title="Tela cheia" onClick={() => { set({ ...st, open: false }); navigate("/chat"); }}>⤢</button>
            <button title="Fechar" onClick={() => set({ ...st, open: false })}>✕</button>
          </div>
          <input className="input" placeholder="Buscar conversa…" value={q} onChange={(e) => setQ(e.target.value)} style={{ margin: "8px 10px", width: "calc(100% - 20px)" }} />
          <div className="dock-list">
            {list.map((c) => (
              <button key={c.id} className="chat-conv" onClick={() => openConv(c.id)}>
                {c.kind === "dm" && c.otherUserId ? <Avatar name={c.title} id={c.otherUserId} size={30} /> : <span className={`chat-ic ${c.kind === "bot" ? "bot" : ""}`}><Icon name={c.kind === "bot" ? "bot" : c.kind === "processo" ? "scale" : c.kind === "caso" ? "folder" : c.kind === "canal" ? "flag" : "chat"} size="sm" /></span>}
                <span className="grow" style={{ minWidth: 0 }}><b className="ell">{c.title}</b>{c.last && <small className="ell muted">{c.last.who}: {c.last.body}</small>}</span>
                {c.unread > 0 && <span className="nav-badge">{c.unread}</span>}
              </button>
            ))}
            {convs && !list.length && <div className="muted small pad">Nenhuma conversa.</div>}
          </div>
        </div>
      )}
      <div className="dock-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="dock-toast" onClick={() => { setToasts((x) => x.filter((y) => y.id !== t.id)); openConv(t.conv.id); }}>
            <b>{t.conv.title}</b>
            {t.conv.last && <span className="ell">{t.conv.last.who}: {t.conv.last.body}</span>}
          </div>
        ))}
      </div>
      <button className={`dock-bubble ${st.open ? "on" : ""}`} title="Chat" onClick={() => set({ ...st, open: !st.open })}>
        <Icon name="chat" />
        {(sum?.unread ?? 0) > 0 && <span className="dotn">{sum!.unread}</span>}
      </button>
      {nova && <NewConvModal onClose={() => setNova(false)} onCreated={(id) => { setNova(false); openConv(id); }} />}
    </>
  );
}
